<template>
  <div
    class="suggestion-popup"
    :style="{ top: `${position.top}px`, left: `${position.left}px` }"
  >
    <!-- Loading State -->
    <div v-if="loading" class="suggestion-popup__loading">
      <v-progress-circular indeterminate size="20" width="2" />
      <span>{{ type === "mention" ? "Searching users..." : "Searching songs..." }}</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="items.length === 0" class="suggestion-popup__empty">
      {{ type === "mention" ? "No users found" : "No songs found" }}
    </div>

    <!-- Items List -->
    <div v-else class="suggestion-popup__items">
      <button
        v-for="(item, index) in items"
        :key="item.id"
        type="button"
        class="suggestion-popup__item"
        :class="{ 'suggestion-popup__item--selected': index === selectedIndex }"
        @click="$emit('select', item)"
        @mouseenter="hoveredIndex = index"
      >
        <!-- Avatar -->
        <div class="suggestion-popup__avatar">
          <v-avatar v-if="item.avatar" size="32">
            <v-img :src="item.avatar" :alt="item.label" />
          </v-avatar>
          <v-avatar v-else size="32" color="primary">
            <v-icon v-if="type === 'mention'">mdi-account</v-icon>
            <v-icon v-else>mdi-music-note</v-icon>
          </v-avatar>
        </div>

        <!-- Content -->
        <div class="suggestion-popup__content">
          <div class="suggestion-popup__label">
            {{ type === "mention" ? "@" : "#" }}{{ item.label }}
          </div>
          <div v-if="item.subtitle" class="suggestion-popup__subtitle">
            {{ item.subtitle }}
          </div>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";

interface SuggestionItem {
  id: string;
  label: string;
  avatar?: string;
  subtitle?: string;
}

defineProps<{
  items: SuggestionItem[];
  loading: boolean;
  selectedIndex: number;
  type: "mention" | "song";
  position: { top: number; left: number };
}>();

defineEmits<{
  select: [item: SuggestionItem];
}>();

const hoveredIndex = ref(-1);
</script>

<style scoped lang="scss">
.suggestion-popup {
  position: fixed;
  z-index: 1000;
  min-width: 200px;
  max-width: 320px;
  max-height: 280px;
  overflow-y: auto;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  &__loading,
  &__empty {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    color: rgba(var(--v-theme-on-surface), 0.6);
    font-size: 0.875rem;
  }

  &__items {
    padding: 4px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    border-radius: 6px;
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover,
    &--selected {
      background: rgba(var(--v-theme-primary), 0.1);
    }
  }

  &__avatar {
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__subtitle {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
